// about vuex
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import config from '../../../../share/config';
import handler from '../../../../share/util/apiResultValidator';
import customTable from '../../../../share/components/vue/PaginationTable';
import resource from '../../../../share/util/resourceMap';
import loading from '../../../../share/components/vue/ProcessingIcon';
import diagnoseResource from '../../../../share/util/diagnose';
import apiService from '../../../../share/util/api/service';

export default {
  name: "customizePage",
  data() {
    return {
      processing: false,
      measuredDataColumns: [],
      measuredDataItems: [],
    };
  },

  watch: {
    language() {
      this.updateColumns();
    },
  },

  components: {
    'custom-table': customTable,
    'processing-view': loading,
  },

  methods: {
    ...mapActions('auth', ['logout']),

    /**
     * カスタマイズ計測データ一覧取得
     * @param {String} projectId プロジェクトID
     * @param {String} measuresId 計測データID
     * @param {String} system ECU名
     */
    async createCstMeasuresList(projectId, measuresId, system) {
      try {
        this.processing = true;
        const params = { region_info: this.cachedProjectDataSource };
        const customizeEndPoint = '/customize/' + projectId + '/' + measuresId;
        const resCustomize = await apiService.getParamCall(apiService.CST, customizeEndPoint, params, this.operationScreen);

        handler.validate(handler.validateTypes.all, resCustomize, this, null, () => {
          this.updateColumns();
          this.measuredDataItems = this.getCustomizeTable(resCustomize.data.projects, system);
        }, (result) => {
          if (result.behaviorType === handler.behaviorTypes.logout) this.logout(this);
        });
      } finally {
        this.processing = false;
      }
    },

    // カラム更新
    async updateColumns() {
      this.measuredDataColumns = [
        { name: 'name', label: 'individual.header_customize_name' },
        { name: 'currentValue', label: 'individual.header_customize_current_setting' },
        { name: 'customizeWriteSettings', label: 'individual.header_customize_write_settings' },
        { name: 'result', label: 'individual.header_customize_result', useResource: true },
        { name: 'description', label: 'individual.common_label_description' },
      ];
    },

    /**
     * カスタマイズの一覧表示.
     * @param {Array} detailData レスポンスのデータ
     * @returns カスタマイズの計測データ一覧
     */
    getCustomizeTable(detailData, system) {
      const param = [];
      if (!detailData || detailData.length < 1) {
        return param;
      }
      return detailData.map(e => ({
        name: this.$t(`cst${system}.${e.immutable_id}.menu_title`),                                                  // 名称
        currentValue: resource.convertCstmDisplayItem(e.id, e.setting.value, system, this, e.data_type),                          // 現在値
        customizeWriteSettings: resource.convertCstmDisplayItem(e.id, e.setting.value, system, this, e.data_type),                // 設定値
        result: this.getCustomizeResult(e.customize_status),                                                         // 結果                                                 
        description: this.$te(`cst${system}.${e.immutable_id}.menu_description`)
          ? this.$t(`cst${system}.${e.immutable_id}.menu_description`) : '-',                                        // 説明
        bgStyle: 'dtc-cursor-default'
      }));
    },

    /**
     * カスタマイズ結果表示文言作成.
     * @param {String} statusId ステータス
     * @returns ステータス表示文言
     */
    getCustomizeResult(statusId) {
      let status = '';
      switch (statusId) {
        case config.CSTM_STATUS_TYPE_SUCCESS:
          status = 'individual.status_customize_result_success';
          break;
        case config.CSTM_STATUS_TYPE_FAILURE:
          status = 'individual.status_customize_result_failure';
          break;
        case config.CSTM_STATUS_TYPE_ERROR:
          status = 'individual.status_customize_result_error';
          break;
        default:
          status = 'message.common_hyphen';
          break;
      }
      return status;
    },

    /**
     * メモボタン押下.
     * DataRecollection.jsのopenMemoModalを呼び出している
     */
    async parentOpenMemoModal() {
      this.$emit('openMemoModal');
    },
  },

  computed: {
    ...mapState({
      language: state => state.systems.language,
      cachedProjectDataSource: state => state.cache.projectDataSource,
    }),

    // 診断系のAPIは複数画面から使用されているため
    // 現在の画面で使用されるAPIが診断/データ再生/個別診断のうちどの機能なのか判別する
    operationScreen() {
      return diagnoseResource.operationFrom(this.$router.currentRoute.value.name);
    }
  },
};